import { useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronBack } from 'react-icons/io5';
import ReadingSheet from './ReadingSheet';
import './ChapterPicker.css';

/**
 * Book → chapter picker. Opens on the current book's chapters, since the
 * common move is jumping within a book; "Books" goes up a level.
 */
const ChapterPicker = ({ open, onClose, chapters, currentChapter, onSelect }) => {
  const [book, setBook] = useState(null); // null = showing the book list
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open) setBook(currentChapter?.book || null);
  }, [open, currentChapter]);

  const testaments = useMemo(() => {
    const groups = [];
    for (const ch of chapters) {
      let group = groups.find(g => g.testament === ch.testament);
      if (!group) {
        group = { testament: ch.testament, books: [] };
        groups.push(group);
      }
      if (!group.books.includes(ch.book)) group.books.push(ch.book);
    }
    return groups;
  }, [chapters]);

  const bookChapters = useMemo(
    () => (book ? chapters.filter(ch => ch.book === book) : []),
    [chapters, book]
  );

  // Keep the current book or chapter in view when a long list opens.
  useEffect(() => {
    if (!open) return;
    bodyRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'center' });
  }, [open, book]);

  const choose = (chapter) => {
    onSelect(chapter.id);
    onClose();
  };

  const backToBooks = (
    <button type="button" className="picker-back" onClick={() => setBook(null)}>
      <IoChevronBack size={18} aria-hidden="true" />
      Books
    </button>
  );

  return (
    <ReadingSheet
      open={open}
      onClose={onClose}
      title={book || 'Choose a book'}
      leading={book ? backToBooks : null}
    >
      <div ref={bodyRef}>
        {book ? (
          <div className="picker-chapters">
            {bookChapters.map(ch => {
              const current = ch.id === currentChapter?.id;
              return (
                <button
                  key={ch.id}
                  type="button"
                  className="picker-chapter"
                  aria-current={current ? 'true' : undefined}
                  aria-label={`${ch.book} ${ch.chapter}`}
                  onClick={() => choose(ch)}
                >
                  {ch.chapter}
                </button>
              );
            })}
          </div>
        ) : (
          testaments.map(group => (
            <section key={group.testament} className="picker-testament">
              <h3>{group.testament} Testament</h3>
              <div className="picker-books">
                {group.books.map(name => (
                  <button
                    key={name}
                    type="button"
                    className="picker-book"
                    aria-current={name === currentChapter?.book ? 'true' : undefined}
                    onClick={() => setBook(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </ReadingSheet>
  );
};

export default ChapterPicker;
